import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Box,
  Grid,
  FormHelperText,
  MenuItem,
  Typography,
  LinearProgress,
  Alert,
  Paper,
} from '@mui/material';
import { t } from 'i18next';
import CustomSelect from 'src/components/forms/theme-elements/CustomSelect';
import CustomFormLabel from 'src/components/forms/theme-elements/CustomFormLabel';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useGenerateScheduleFromSheetMutation } from 'src/services/tournaments';
import { useParams } from 'react-router';
import useToast from 'src/hooks/useToast';
import { useSelector } from 'src/store/Store';
import { CloudUpload, Download } from '@mui/icons-material';
import * as XLSX from 'xlsx';

const SwapTeamsForm = ({
  open,
  onClose,
  loadMatches,
}: {
  open: boolean;
  onClose: () => void;
  loadMatches: () => void;
}) => {
  const { id } = useParams();
  const { toastPromise } = useToast();

  const selectedTournament = useSelector((state) => state.tournamentReducer.selectedTournament);

  const [generateScheduleFromSheet] = useGenerateScheduleFromSheetMutation();
  const [dragActive, setDragActive] = useState(false);
  const [fileError, setFileError] = useState('');
  const [uploading, setUploading] = useState(false);

  const formik = useFormik({
    initialValues: {
      categoryId: undefined,
      file: undefined,
    },
    validationSchema: Yup.object({
      categoryId: Yup.number().required(t('TournamentScheduleTab.categoryIdRequired')),
      file: Yup.mixed()
        .required(t('TournamentScheduleTab.fileRequired'))
        .test('fileType', t('TournamentScheduleTab.invalidFileType'), (value) => {
          if (!value) return false;

          return value.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        }),
    }),
    onSubmit: (values) => {
      setUploading(true);
      toastPromise({
        request: () => {
          const formData = new FormData();
          formData.append('file', values.file);
          formData.append('category_id', values.categoryId.toString());

          return generateScheduleFromSheet({
            tournamentId: Number(id),
            body: formData,
          });
        },
        onSuccess: (result) => {
          if ('error' in result) {
            throw result.error;
          }

          const selectedCategory = selectedTournament?.categories?.find(
            (cat) => cat.id === values.categoryId,
          );
          onClose();
          loadMatches();
          setUploading(false);

          return t('TournamentScheduleTab.scheduleUploadedSuccessfully', {
            category: selectedCategory?.name || '',
            categoryType:
              selectedCategory?.category_type_name ||
              selectedTournament?.tournament_type?.name ||
              '',
          });
        },
        onError: (err) => {
          setUploading(false);
          if (err.data && err.data.errors) {
            const errorMessages = Object.values(err.data.errors).flat().join('. ');

            return errorMessages;
          }
          if (err?.data?.message) {
            return err?.data?.message;
          }

          return t('TournamentScheduleTab.scheduleUploadFailed');
        },
      });
    },
  });

  const validateFileStructure = (file: File): Promise<string | null> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          if (jsonData.length < 2) {
            resolve(t('TournamentScheduleTab.emptyFile'));

            return;
          }

          const headers = jsonData[0] as string[];
          const requiredFields = ['Team_1 ID', 'Team_2 ID', 'Group Number'];

          for (const field of requiredFields) {
            if (!headers.includes(field)) {
              resolve(t('TournamentScheduleTab.missingRequiredColumn', { column: field }));

              return;
            }
          }

          resolve(null);
        } catch (error) {
          resolve(t('TournamentScheduleTab.invalidFileFormat'));
        }
      };
      reader.readAsBinaryString(file);
    });
  };

  const handleFileChange = async (file: File) => {
    setFileError('');

    if (!file.type.includes('spreadsheetml')) {
      setFileError(t('TournamentScheduleTab.onlyXlsxAllowed'));

      return;
    }

    const structureError = await validateFileStructure(file);
    if (structureError) {
      setFileError(structureError);

      return;
    }

    formik.setFieldValue('file', file);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileChange(e.dataTransfer.files[0]);
    }
  };

  const downloadTemplate = () => {
    const templateData = [['Team_1 ID', 'Team_2 ID', 'Group Number', 'Round']];

    const worksheet = XLSX.utils.aoa_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Schedule Template');
    XLSX.writeFile(workbook, 'schedule_template.xlsx');
  };

  const handleClose = () => {
    onClose();
    formik.resetForm();
    setFileError('');
    setUploading(false);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle>{t('TournamentScheduleTab.customGenerateSchedule')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('TournamentScheduleTab.generateScheduleForm')}</DialogContentText>

          {uploading && (
            <Box mt={2}>
              <LinearProgress />
              <Typography variant="body2" sx={{ mt: 1 }}>
                {t('TournamentScheduleTab.uploadingFile')}
              </Typography>
            </Box>
          )}

          <Box mt={2}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <CustomFormLabel htmlFor="categoryId">
                  {t('TournamentScheduleTab.category')}
                </CustomFormLabel>
                <CustomSelect
                  id="categoryId"
                  fullWidth
                  {...formik.getFieldProps('categoryId')}
                  error={formik.touched.categoryId && Boolean(formik.errors.categoryId)}
                  helperText={formik.touched.categoryId && formik.errors.categoryId}
                >
                  {selectedTournament?.categories?.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </CustomSelect>
                {formik.touched.categoryId && formik.errors.categoryId && (
                  <FormHelperText error>{formik.errors.categoryId}</FormHelperText>
                )}
              </Grid>

              <Grid item xs={12}>
                <CustomFormLabel>{t('TournamentScheduleTab.uploadScheduleFile')}</CustomFormLabel>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    border: dragActive ? '2px dashed #1976d2' : '2px dashed #ccc',
                    backgroundColor: dragActive ? '#f5f5f5' : 'transparent',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                  }}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  onClick={() => document.getElementById('file-input')?.click()}
                >
                  <CloudUpload sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    {formik.values.file
                      ? formik.values.file.name
                      : t('TournamentScheduleTab.dragDropFile')}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t('TournamentScheduleTab.onlyXlsxSupported')}
                  </Typography>
                  <input
                    id="file-input"
                    type="file"
                    accept=".xlsx"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        handleFileChange(e.target.files[0]);
                      }
                    }}
                  />
                </Paper>

                {(fileError || (formik.touched.file && formik.errors.file)) && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {fileError || formik.errors.file}
                  </Alert>
                )}

                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<Download />}
                    onClick={downloadTemplate}
                    size="small"
                  >
                    {t('TournamentScheduleTab.downloadTemplate')}
                  </Button>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ mt: 1, display: 'block' }}
                  >
                    {t('TournamentScheduleTab.teamIdsNote')}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ pb: 2 }}>
          <Button color="error" onClick={handleClose} disabled={uploading}>
            {t('Common.cancel')}
          </Button>
          <Button type="submit" disabled={uploading || !formik.isValid}>
            {t('TournamentScheduleTab.generateSchedule')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default SwapTeamsForm;
